/* outstanding_candidate_interests.js — live admin list of candidate interest
   indicators awaiting conversion into nominees.

   Subscribes to OutstandingCandidateInterestsChannel. A `candidate_interest_created`
   event prepends a row the instant an owner submits the public self-nomination
   form; `candidate_interest_resolved` removes the row once any admin converts it.
   Row markup mirrors views/dashboards/_candidate_interest_row.html.erb — keep the
   two in step. */
document.addEventListener('turbolinks:load', () => {
  const panel = document.getElementById('outstanding-candidate-interests');
  if (!panel) return;
  if (panel.dataset.subscribed === 'true') return;

  const list     = document.getElementById('cii-list');
  const empty    = document.getElementById('cii-empty');
  const countEl  = document.getElementById('cii-count');
  const dotEl    = document.getElementById('cii-live-dot');
  const labelEl  = document.getElementById('cii-live-label');
  const badgeEl  = document.getElementById('cii-live-badge');
  if (!list) return;

  const esc = (str) =>
    (str == null ? '' : String(str))
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const rowId = (referenceId) => 'cii-row-' + referenceId;

  function setLive(isLive) {
    if (!dotEl || !labelEl || !badgeEl) return;

    dotEl.className = isLive ? 'w-1.5 h-1.5 rounded-full bg-emerald-500' : 'w-1.5 h-1.5 rounded-full bg-gray-400';
    labelEl.textContent = isLive ? 'Live' : 'Offline';
    badgeEl.className = isLive
      ? 'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200'
      : 'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200';
  }

  /* Prefer the count the server sent — it is the authoritative one, and it also
     covers indicators the browser has never rendered. Fall back to the DOM. */
  function refreshCount(serverCount) {
    const rows = list.querySelectorAll('.cii-row').length;

    if (countEl) {
      countEl.textContent = (typeof serverCount === 'number') ? serverCount : rows;
    }
    if (empty) empty.classList.toggle('hidden', rows > 0);
  }

  function unitBadge(data) {
    if (data.unit_identifier) {
      return '<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#f3e8ff] text-[#491363] border border-[#e4dce9]">Unit ' +
             esc(data.unit_identifier) + '</span>';
    }
    return '<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">Unit unassigned</span>';
  }

  function rowHtml(data) {
    const href    = esc(data.path || '#');
    const contact = [data.email, data.unit_address].filter(Boolean).map(esc).join(' &middot; ');

    return [
      '<div class="min-w-0">',
        '<div class="flex items-center gap-2 flex-wrap">',
          '<a href="', href, '" class="text-sm font-semibold text-[#491363] hover:underline">',
            esc(data.full_name || 'Unnamed candidate'),
          '</a>',
          unitBadge(data),
        '</div>',
        '<p class="text-xs text-gray-600 mt-1 truncate">', esc(data.order_name), '</p>',
        '<p class="text-xs text-gray-400 mt-0.5 truncate">', contact, '</p>',
      '</div>',
      '<div class="flex items-center gap-3 flex-shrink-0">',
        '<span class="text-xs text-gray-400 whitespace-nowrap">', esc(data.submitted_at), '</span>',
        '<a href="', href, '" class="inline-flex items-center gap-1.5 text-xs font-medium text-white rounded-lg px-3 py-1.5" ',
           'style="background: linear-gradient(135deg, #491363, #a146b4)">Review</a>',
      '</div>'
    ].join('');
  }

  function addRow(data) {
    if (!data.reference_id) return;

    let row = document.getElementById(rowId(data.reference_id));
    const isNew = !row;

    if (isNew) {
      row = document.createElement('div');
      row.id = rowId(data.reference_id);
      row.className = 'cii-row px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ' +
                      'border-b border-[#f1eaf5] last:border-0 hover:bg-[#faf7fc] transition-colors animate-flash-autofill';
    }

    row.innerHTML = rowHtml(data);
    if (isNew) list.prepend(row);
  }

  function removeRow(referenceId) {
    const row = referenceId && document.getElementById(rowId(referenceId));
    if (row) row.remove();
  }

  if (!window.App || !window.App.cable) {
    // No consumer on the page (asset build issue, or cable disabled) — the list
    // the server rendered is still correct, so say so rather than lying "Live".
    setLive(false);
    return;
  }

  window.App.cable.subscriptions.create('OutstandingCandidateInterestsChannel', {
    connected() {
      setLive(true);
    },

    disconnected() {
      setLive(false);
    },

    rejected() {
      setLive(false);
    },

    received(data) {
      if (!data) return;

      if (data.type === 'candidate_interest_created') {
        addRow(data);
      } else if (data.type === 'candidate_interest_resolved') {
        removeRow(data.reference_id);
      } else {
        return;
      }

      refreshCount(data.outstanding_count);
    }
  });

  panel.dataset.subscribed = 'true';
});
